import { defineStore } from 'pinia';
import { ref, nextTick } from 'vue';

// eslint-disable-next-line import/prefer-default-export
export const useSnackbarStore = defineStore('snackbar', () => {
  const isVisible = ref(false);
  const message = ref('');
  const color = ref('deep-purple-accent-4');
  const multiLine = ref(false);
  const actionText = ref('');
  const actionCallback = ref(null);

  //how long should the snackbar be visible?
  const timeoutMilliseconds = 5000;

  // A prior call's auto-hide timeout wasn't cleared, so calling showSnackbar
  // again while one was already visible could still get hidden early by the
  // EARLIER call's timer, regardless of the new call's own timeout.
  let hideTimeoutId = null;

  // Function to trigger the snackbar
  function showSnackbar(newColor = 'deep-purple-accent-4', newMessage, options = {}) {
    clearTimeout(hideTimeoutId);
    message.value = newMessage;
    color.value = newColor;
    multiLine.value = options.multiLine || false;
    actionText.value = options.actionText || '';
    actionCallback.value = options.actionCallback || null;

    // Force a real false->true transition rather than a same-value no-op:
    // VOverlay/VSnackbar's `useProxiedModel` chain (3 layers deep) sometimes
    // fails to propagate isVisible=true into the `v-overlay--active` class,
    // reproducible even on unmodified production code (confirmed live via
    // Vue devtools + a MutationObserver: isVisible read back `true`, but the
    // active class never got applied - an intermittent race, not always).
    // A prior attempt at this same false/true flip - before SnackbarComponent
    // had `eager` - broke rendering outright, because VOverlay's Teleported
    // content is lazily booted and the flip happened too fast for that boot
    // to land. With `eager` keeping the content permanently mounted, this
    // flip only needs to toggle the `active` class, which is safe.
    isVisible.value = false;
    nextTick(() => {
      isVisible.value = true;
    });
    const timeout = options.timeout || timeoutMilliseconds;
    hideTimeoutId = setTimeout(() => {
      isVisible.value = false;
    }, timeout);
  }
  function executeAction() {
    if (actionCallback.value && typeof actionCallback.value === 'function') {
      actionCallback.value();
    }
    isVisible.value = false;
  }
  function closeSnackbar() {
    isVisible.value = false;
  }
  return {
    isVisible,
    message,
    color,
    multiLine,
    actionText,
    showSnackbar,
    executeAction,
    closeSnackbar
  };
});